/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified delay has elapsed since the last time the
 * debounced function was invoked.
 *
 * Derived from https://github.com/bjoerge/debounce-promise/blob/master/index.js
 *
 * @template A - The type of the arguments for the function.
 * @template R - The type of the return value of the function.
 * @param {Function} fn - The function to debounce.
 * @param {number | (() => number)} [wait=0] - The number of milliseconds to delay.
 * @param {DebounceOptions} [options={}] - The options for the debounce.
 * @returns {(...args: A) => Promise<R>} A new debounced function.
 */
export function debouncePromise<A extends any[], R>(
  fn: (...args: A) => R,
  wait: number | (() => number) = 0,
  options: DebounceOptions = {}
): (...args: A) => Promise<R> {
  let lastCallAt: number | undefined;
  let deferred: Deferred<R> | undefined;
  let timer: number | undefined;
  let pendingArgs: A[] = [];

  function getWait(wait: number | (() => number)): number {
    return typeof wait === 'function' ? wait() : wait;
  }

  function flush(this: any) {
    const thisDeferred = deferred!;
    clearTimeout(timer);

    Promise.resolve(
      options.accumulate
        ? fn.call(this, pendingArgs as unknown as A)
        : fn.call(this, ...pendingArgs[pendingArgs.length - 1])
    ).then(thisDeferred.resolve, thisDeferred.reject);

    pendingArgs = [];
    deferred = undefined;
  }

  function debounced(this: any, ...args: A): Promise<R> {
    const currentWait = getWait(wait);
    const currentTime = Date.now();

    const isCold = !lastCallAt || currentTime - lastCallAt > currentWait;

    lastCallAt = currentTime;

    if (isCold && options.leading) {
      return Promise.resolve(
        options.accumulate ? (fn as any).call(this, [args]) : fn.call(this, ...args)
      ).then(result => (options.accumulate ? (result as any)[0] : result));
    }

    if (deferred) {
      clearTimeout(timer);
    } else {
      deferred = defer<R>();
    }

    pendingArgs.push(args);
    timer = setTimeout(flush.bind(this), currentWait);

    if (options.accumulate) {
      const argsIndex = pendingArgs.length - 1;
      return deferred.promise.then(results => (results as any)[argsIndex]);
    }

    return deferred.promise;
  }

  return debounced;
}

function defer<R>(): Deferred<R> {
  const deferred: Partial<Deferred<R>> = {};
  deferred.promise = new Promise<R>((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred as Deferred<R>;
}

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}

interface DebounceOptions {
  leading?: boolean;
  accumulate?: boolean;
}
